import moment from 'moment'

import { Dashboard } from '../../models/Dashboard'
import { Gift } from '../../models/Gift'
import { RecordFactory } from '../../models/RecordFactory'
import { getViewTitleFromView } from '../../utils/dashboard'
import { currencyFormat } from '../../utils/numbers'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import Tag from '../app/design/Tag'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    gifts: Gift[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

function GiftBalance({ gift }: { gift: Gift }) {
    if (!gift.paidDate) {
        return <Tag label="Not paid" intent="muted" className="w-fit" size="sm" />
    }

    if ((gift.balance ?? 0) <= 0 || moment(gift.expiredDate).isBefore(moment())) {
        return <Tag label="Expired" intent="danger" className="w-fit" size="sm" />
    }

    if ((gift.balance ?? 0) <= (gift.total ?? 0) / 10) {
        return (
            <Tag
                label={currencyFormat(gift.balance)}
                intent="warning"
                className="w-fit"
                size="sm"
            />
        )
    }

    return <Tag label={currencyFormat(gift.balance)} intent="success" className="w-fit" size="sm" />
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.gifts.map((g) => RecordFactory.getInstance().loadGift(g.id!))
    )
}

function render({ base }: PocketComponentProps<void, Props>) {
    const { gifts, dashboard, view, searchText, isLoading } = base.props

    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} ${
                            gifts.length === 1 ? `gift certificate` : `gift certificates`
                        }`,
                        value: gifts.length.toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Gift</TableHead>
                        <TableHead className="font-normal">Balance</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {gifts.map((g) => (
                        <ClickableTableRow key={g.id} href={`/gifts/${g.id}`}>
                            <TableCell>
                                <ClientTableCell user={g.customer} subaccount={g.subaccount} />
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm">{g.name}</Paragraph>
                            </TableCell>
                            <TableCell>
                                <GiftBalance gift={g} />
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {gifts.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const GiftsDashboard = usePSComponent(render, viewWillAppear)
